import React, { useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { useHistory, useParams } from 'react-router-dom';
import { NULL_USER } from '../../lib/user';
import { deepRemoveKeys } from '../../lib/deepRemoveKeys';
import { reportError } from '../../lib/reportError';
import { PaymentSource } from '../../constants';
import { hrefFor } from '../../routers';
import {
  useCart,
  useLegacyUpdateCartMutation,
  LegacyCartUpdatePropertyName,
  LegacyCartUpdateAction,
  CartVersion
} from '../../hooks/useCart';
import { usePlaceOrderMutation } from '../../hooks/useCheckout/usePlaceOrderMutation';
import { useChargeOrder } from '../../hooks/useChargeOrder';
import { useErrorModal } from '../../hooks/useErrorModal';
import { useUser } from '../../hooks/useUser';
import { useOrderSummary } from '../../hooks/useOrderSummary';
import { OrderState } from '../../hooks/useUpdateOrder';
import { usePaymentError } from '../../hooks/useCheckout/usePaymentError';
import {
  CreateApplePayMerchantSessionMutation,
  CreateApplePayMerchantSessionMutationVariables,
  CREATE_APPLE_PAY_MERCHANT_SESSION
} from './createApplePayMerchantSession.rest';

const stringToNumStatus = (key: string) =>
  ((ApplePaySession as unknown) as { [key: string]: number })[key];

const OnValidateMerchant = ({ session }: { session: ApplePaySession }) => {
  const [onCreateApplePayMerchantSessionHandler] = useMutation<
    CreateApplePayMerchantSessionMutation,
    CreateApplePayMerchantSessionMutationVariables
  >(CREATE_APPLE_PAY_MERCHANT_SESSION);
  const { showErrorModal } = useErrorModal();

  useEffect(() => {
    session.onvalidatemerchant = async event => {
      try {
        const { data } = await onCreateApplePayMerchantSessionHandler({
          variables: {
            input: {
              url: event.validationURL
            }
          }
        });

        session.completeMerchantValidation(data?.applePayMerchantSession);
      } catch (error) {
        reportError(error);
        showErrorModal();
      }
    };
  }, [onCreateApplePayMerchantSessionHandler, session, showErrorModal]);

  return null;
};

const OnShippingContactSelected = ({ session }: { session: ApplePaySession }) => {
  const { showErrorModal } = useErrorModal();
  const [updateLegacyCart] = useLegacyUpdateCartMutation();

  useEffect(() => {
    session.onshippingcontactselected = async event => {
      const result = await updateLegacyCart(LegacyCartUpdateAction.UpdateCart, {
        [LegacyCartUpdatePropertyName.RenderedBy]: CartVersion.Checkout,
        [LegacyCartUpdatePropertyName.PaymentSource]: PaymentSource.Apple,
        address: {
          [LegacyCartUpdatePropertyName.State]: event.shippingContact.administrativeArea?.toUpperCase(),
          [LegacyCartUpdatePropertyName.City]: event.shippingContact.locality,
          [LegacyCartUpdatePropertyName.Phone]: '',
          [LegacyCartUpdatePropertyName.PostalCode]: event.shippingContact.postalCode,
          [LegacyCartUpdatePropertyName.CountryCode]: event.shippingContact.countryCode?.toUpperCase(),
          [LegacyCartUpdatePropertyName.AddressLine1]: '',
          [LegacyCartUpdatePropertyName.AddressLine2]: '',
          [LegacyCartUpdatePropertyName.FirstName]: '',
          [LegacyCartUpdatePropertyName.LastName]: '',
          [LegacyCartUpdatePropertyName.Default]: false
        }
      });
      const newCart = result.data?.legacyUpdateCart.data[0];
      const newApplePaySummary = deepRemoveKeys(
        newCart?.attributes.applePaymentSummary,
        '__typename'
      );
      if (newApplePaySummary && Number(newApplePaySummary.data.total.amount) > 0) {
        session.completeShippingContactSelection(
          stringToNumStatus(newApplePaySummary.data.status.code),
          newApplePaySummary?.data.shippingMethods,
          newApplePaySummary?.data.total,
          newApplePaySummary?.data.lineItems
        );
      } else {
        showErrorModal();
        session.abort();
      }
    };
  }, [session, showErrorModal, updateLegacyCart]);

  return null;
};

const OnShippingMethodSelected = ({ session }: { session: ApplePaySession }) => {
  const { showErrorModal } = useErrorModal();
  const [updateLegacyCart] = useLegacyUpdateCartMutation();

  useEffect(() => {
    session.onshippingmethodselected = async event => {
      const result = await updateLegacyCart(LegacyCartUpdateAction.UpdateCart, {
        [LegacyCartUpdatePropertyName.ShippingMethod]: parseInt(
          event.shippingMethod.identifier || '',
          10
        )
      });

      const newCart = result.data?.legacyUpdateCart.data[0];
      const newApplePaySummary = deepRemoveKeys(
        newCart?.attributes.applePaymentSummary,
        '__typename'
      );

      if (newApplePaySummary && Number(newApplePaySummary.data.total.amount) > 0) {
        session.completeShippingMethodSelection(
          stringToNumStatus(newApplePaySummary.data.status.code),
          newApplePaySummary.data.total,
          newApplePaySummary.data.lineItems
        );
      } else {
        showErrorModal();
        session.abort();
      }
    };
  }, [session, showErrorModal, updateLegacyCart]);

  return null;
};

const OnPaymentAuthorized = ({ session }: { session: ApplePaySession }) => {
  const history = useHistory();
  const [placeOrder] = usePlaceOrderMutation();
  const {
    cart: {
      id,
      attributes: { countryCode, applePaymentSummary }
    }
  } = useCart();
  const {
    setUserData,
    user: { isLoggedIn, firstName, email }
  } = useUser();
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const { orderSummary } = useOrderSummary(orderNumber);
  const { chargeOrder } = useChargeOrder(orderNumber);
  const { setPaymentError } = usePaymentError();
  const { showErrorModal } = useErrorModal();

  useEffect(() => {
    session.onpaymentauthorized = async event => {
      const paymentDetails = {
        card_type: event.payment.token.paymentMethod.network,
        last_four: event.payment.token.paymentMethod.displayName.replace(/[^\d.]/g, ''),
        source: PaymentSource.Apple
      };

      const billingAddress = {
        billing_address: {
          city: event.payment.billingContact?.locality,
          state: event.payment.billingContact?.administrativeArea?.toUpperCase(),
          postal_code: event.payment.billingContact?.postalCode,
          country_code: event.payment.billingContact?.countryCode?.toUpperCase(),
          address_line1: event.payment.billingContact?.addressLines?.[0],
          address_line2: event.payment.billingContact?.addressLines?.[1],
          first_name: event.payment.billingContact?.givenName,
          last_name: event.payment.billingContact?.familyName
        }
      };

      const shippingAddress = {
        city: event.payment.shippingContact?.locality,
        state: event.payment.shippingContact?.administrativeArea?.toUpperCase(),
        phone: '',
        postal_code: event.payment.shippingContact?.postalCode,
        country_code: event.payment.shippingContact?.countryCode?.toUpperCase(),
        address_line1: event.payment.shippingContact?.addressLines?.[0],
        address_line2: event.payment.shippingContact?.addressLines?.[1],
        first_name:
          event.payment.shippingContact?.givenName ||
          firstName ||
          event.payment.shippingContact?.emailAddress?.split('@')?.[0],
        last_name: event.payment.shippingContact?.familyName,
        email: event.payment.shippingContact?.emailAddress,
        default: false
      };

      const applePayCart = {
        is_guest: !isLoggedIn,
        country_code: countryCode,
        cart_id: id,
        address: shippingAddress,
        payment_source: PaymentSource.Apple,
        payment_token: btoa(JSON.stringify(event.payment.token.paymentData)),
        other_payment_details: paymentDetails,
        payment_summary: billingAddress,
        ...(applePaymentSummary?.data.shippingMethods &&
          applePaymentSummary.data.shippingMethods[0] && {
            shipment_method: applePaymentSummary.data.shippingMethods[0].identifier
          })
      };

      const applePaySecondPaymentOrder = {
        id: orderSummary?.id,
        payment_source: PaymentSource.Apple,
        payment_id: orderSummary?.attributes.selectedPaymentId,
        selected_items_ids: orderSummary?.attributes.selectedItemsIds,
        country_code: countryCode,
        use_site_credit: orderSummary?.attributes.useSiteCredit,
        use_reward_points: orderSummary?.attributes.useMoRewards,
        order_state: OrderState.Charge,
        payment_token: btoa(JSON.stringify(event.payment.token.paymentData)),
        other_payment_details: paymentDetails,
        payment_summary: billingAddress
      };

      if (!orderNumber) {
        try {
          const { data } = await placeOrder(applePayCart);
          const orderData = data?.checkout.data[0].attributes;

          if (orderData && orderData.guestAccessToken) {
            setUserData({
              ...NULL_USER,
              email: orderData.userEmail || event.payment.shippingContact?.emailAddress || '',
              accessToken: orderData.guestAccessToken,
              isMember: orderData.userIsMember || false,
              remember: true
            });
          }

          session.completePayment(ApplePaySession.STATUS_SUCCESS);
          orderData &&
            history.push(
              hrefFor.OrderConfirmationPage({
                orderNumber: orderData.orderNumber
              })
            );
        } catch (error) {
          session.completePayment(ApplePaySession.STATUS_FAILURE);
          if (error.reason !== 'canceled') {
            const networkErrorMessage = error.networkError?.result?.errors?.[0]?.detail;
            const message =
              networkErrorMessage ||
              error.displayMessage ||
              'An error occurred. Please retry later.';
            reportError(error);
            setPaymentError(message ?? null);
            showErrorModal(message);
            window.newrelic?.addPageAction('paymentMethodFailed', {
              error: error.toString(),
              networkErrorMessage,
              message,
              email: email,
              guest: !isLoggedIn
            });
          }
        }
      } else {
        const response = await chargeOrder(applePaySecondPaymentOrder);

        const orderData = response && response.data?.chargeOrder.data[0].attributes;
        const errorMessage = response && response.data?.chargeOrder.data[0].attributes.message;
        const hasError =
          response &&
          response.data?.chargeOrder.data[0].attributes.errors &&
          response.data?.chargeOrder.data[0].attributes.errors.length > 0;

        if (errorMessage && hasError) {
          session.completePayment(ApplePaySession.STATUS_FAILURE);
          window.newrelic?.addPageAction('secondPaymentFailed', {
            error: errorMessage,
            email: email,
            guest: !isLoggedIn,
            paymentSource: orderSummary?.attributes.paymentMethod.source,
            payToday: orderSummary?.attributes.paymentSummary.payToday.price,
            total: orderSummary?.attributes.paymentSummary.total.price
          });
          setPaymentError(errorMessage ?? null);
          showErrorModal(errorMessage);
        }

        if (!hasError && orderData) {
          session.completePayment(ApplePaySession.STATUS_SUCCESS);
          history.push(
            hrefFor.OrderDetailPage({
              orderNumber: orderData.orderNumber
            })
          );
        }
      }
    };
  }, [
    applePaymentSummary,
    chargeOrder,
    countryCode,
    firstName,
    email,
    history,
    id,
    isLoggedIn,
    orderNumber,
    orderSummary,
    placeOrder,
    session,
    setUserData,
    setPaymentError,
    showErrorModal
  ]);

  return null;
};

export const SetupApplePaySession = ({ session }: { session: ApplePaySession }) => {
  useEffect(() => {
    session.begin();
  }, [session]);

  const { orderNumber } = useParams<{ orderNumber: string }>();

  return (
    <>
      <OnValidateMerchant session={session} />
      {!orderNumber && (
        <>
          <OnShippingContactSelected session={session} />
          <OnShippingMethodSelected session={session} />
        </>
      )}
      <OnPaymentAuthorized session={session} />
    </>
  );
};
